import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Helmet } from 'react-helmet';
import { useDispatch } from 'react-redux';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { setLanguage } from '../../../features/langSlice';
import { Icon } from '../../elements/icon';
import { Accordion } from '../../elements/Accordion';
import CustomLink from '../../CustomLink';
import ExpertSection from '../../Customer/components/ExpertSection';
import ReviewsMobile from '../../Customer/components/ReviewsMobile';
import BoomTestimonials from '../../Customer/components/BoomTestimonials';
import MakeChoice from '../../Home/MakeChoice';
import IngredientsSlider from '../../Home/Slider/IngredientsSlider';
import DiscountBanner from '../../Home/DiscountBanner';
import useReviewStats from '../../../hooks/useReviewStats';
import { Review1, Review2, Review3, Review4, ReviewRating } from '../../../Constants';
import useMeta from '../../../hooks/useMeta';
import { logo_home } from '../../../Data/Images/Home';
import '../../../assets/css/landing-pages.css';
import HeroImage from '../../../assets/hero-lp-pills.webp';
import RiisePouch from '../../../assets/yellow-paper-pouch-granules.webp';
import JanPhoto from '../../../assets/jan-testimonial.jpg';

const K = {
  HeroH1Part1:         'LP_Combo_HeroH1Part1',
  HeroH1Part2:         'LP_Combo_HeroH1Part2',
  HeroSubline:         'LP_Combo_HeroSubline',
  HeroCta:             'LP_Combo_HeroCta',
  HeroSocialProof:     'LP_Combo_HeroSocialProof',
  Stat1Val:            'LP_Combo_Stat1Val',
  Stat1Label:          'LP_Combo_Stat1Label',
  Stat2Val:            'LP_Combo_Stat2Val',
  Stat2Label:          'LP_Combo_Stat2Label',
  Stat3Val:            'LP_Combo_Stat3Val',
  Stat3Label:          'LP_Combo_Stat3Label',
  Stat4Val:            'LP_Combo_Stat4Val',
  Stat4Label:          'LP_Combo_Stat4Label',
  ProbSectionLabel:    'LP_Combo_ProbSectionLabel',
  ProbH2Line1:         'LP_Combo_ProbH2Line1',
  ProbH2Line2:         'LP_Combo_ProbH2Line2',
  ProbSubline:         'LP_Combo_ProbSubline',
  OldWayTitle:         'LP_Combo_OldWayTitle',
  OldWay1:             'LP_Combo_OldWay1',
  OldWay2:             'LP_Combo_OldWay2',
  OldWay3:             'LP_Combo_OldWay3',
  OldWay4:             'LP_Combo_OldWay4',
  OldWay5:             'LP_Combo_OldWay5',
  NewWayTitle:         'LP_Combo_NewWayTitle',
  NewWay1:             'LP_Combo_NewWay1',
  NewWay2:             'LP_Combo_NewWay2',
  NewWay3:             'LP_Combo_NewWay3',
  NewWay4:             'LP_Combo_NewWay4',
  NewWay5:             'LP_Combo_NewWay5',
  ResultsSectionLabel: 'LP_Combo_ResultsSectionLabel',
  ResultsH1:           'LP_Combo_ResultsH1',
  ResultsH2:           'LP_Combo_ResultsH2',
  ResultsStat1Val:     'LP_Combo_ResultsStat1Val',
  ResultsStat1Desc:    'LP_Combo_ResultsStat1Desc',
  ResultsStat2Val:     'LP_Combo_ResultsStat2Val',
  ResultsStat2Desc:    'LP_Combo_ResultsStat2Desc',
  ResultsSource:       'LP_Combo_ResultsSource',
  HowWorksHeading:     'LP_Combo_HowWorksHeading',
  Step1Title:          'LP_Combo_Step1Title',
  Step1Desc:           'LP_Combo_Step1Desc',
  Step2Title:          'LP_Combo_Step2Title',
  Step2Desc:           'LP_Combo_Step2Desc',
  Step3Title:          'LP_Combo_Step3Title',
  Step3Desc:           'LP_Combo_Step3Desc',
  ExpertHeading:       'LP_Combo_ExpertHeading',
  IngrHeading:         'LP_Combo_IngrHeading',
  FeatQuote:           'LP_Combo_FeatQuote',
  FeatAuthor:          'LP_Combo_FeatAuthor',
  ReviewsHeading:      'LP_Combo_ReviewsHeading',
  Faq1Q:               'LP_Combo_Faq1Q',
  Faq1A:               'LP_Combo_Faq1A',
  Faq2Q:               'LP_Combo_Faq2Q',
  Faq2A:               'LP_Combo_Faq2A',
  Faq3Q:               'LP_Combo_Faq3Q',
  Faq3A:               'LP_Combo_Faq3A',
  Faq4Q:               'LP_Combo_Faq4Q',
  Faq4A:               'LP_Combo_Faq4A',
  Faq5Q:               'LP_Combo_Faq5Q',
  Faq5A:               'LP_Combo_Faq5A',
  FaqHeading:          'LP_Combo_FaqHeading',
  FinalHeading:        'LP_Combo_FinalHeading',
  FinalSubline:        'LP_Combo_FinalSubline',
  FinalCta:            'LP_Combo_FinalCta',
  AdDisclosure:        'LP_Combo_AdDisclosure',
  BadgeLabelLine1:     'LP_BadgeLabelLine1',
  BadgeLabelLine2:     'LP_BadgeLabelLine2',
  BadgeText:           'LP_BadgeText',
  CtaSub:              'LP_CtaSub',
};

const SERIF = { fontFamily: "'DM Serif Display', serif" };
const SANS  = { fontFamily: "'Plus Jakarta Sans', sans-serif" };

const CtaButton = ({ children, full = true }) => {
  const { metaEvent } = useMeta();
  return (
  <CustomLink
    to="/quiz"
    onClick={() => metaEvent('Lead')}
    className="lp-btn-cta"
    style={{ ...SANS, display: 'flex', alignItems: 'center', justifyContent: 'center',
      gap: 8, background: '#F6E147', color: '#103642',
      fontWeight: 700, fontSize: 16, borderRadius: 999,
      padding: '18px 36px', textDecoration: 'none',
      width: full ? '100%' : 'auto' }}
  >
    {children}
    <span aria-hidden="true">→</span>
  </CustomLink>
  );
};

const Label = ({ children, pill = false }) => {
  const base = {
    ...SANS, fontSize: 11, fontWeight: 700, letterSpacing: '0.14em',
    textTransform: 'uppercase', color: '#116D6E',
    display: 'block', textAlign: 'center', margin: 0,
  };
  if (pill) return (
    <div style={{ display: 'flex', justifyContent: 'center' }}>
      <span style={{ ...base, background: 'rgba(17,109,110,0.1)', borderRadius: 999, padding: '6px 16px' }}>
        {children}
      </span>
    </div>
  );
  return <p style={base}>{children}</p>;
};

const StepNumber = ({ n }) => (
  <div style={{ flexShrink: 0, width: 40, height: 40, borderRadius: '50%',
    background: '#103642', color: '#F6E147', ...SANS,
    fontWeight: 700, fontSize: 15, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
    {n}
  </div>
);

const StarRow = ({ count = 5 }) => (
  <div style={{ display: 'flex', gap: 4 }}>
    {Array.from({ length: count }).map((_, i) => (
      <svg key={i} width="18" height="18" viewBox="0 0 20 20" fill="#F6E147">
        <path d="M10 1l2.39 4.84L18 6.75l-4 3.9.94 5.5L10 13.77l-4.94 2.38L6 10.65 2 6.75l5.61-.91L10 1z" />
      </svg>
    ))}
  </div>
);

const Section = ({ bg = '#fff', py = 64, children, style = {}, maxW = 480, px = 24 }) => (
  <section style={{ background: bg, padding: `${py}px ${px}px`, ...style }}>
    <div style={{ maxWidth: maxW, margin: '0 auto' }}>
      {children}
    </div>
  </section>
);

const Stack = ({ gap = 24, align = 'stretch', children, style = {} }) => (
  <div style={{ display: 'flex', flexDirection: 'column', gap, alignItems: align, ...style }}>
    {children}
  </div>
);

const LangSwitcher = () => {
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState(localStorage.getItem('lang') || 'de');
  const { i18n } = useTranslation();
  const dispatch = useDispatch();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const ref = useRef(null);

  useEffect(() => {
    const handleClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const switchTo = (lng) => {
    localStorage.setItem('lang', lng);
    i18n.changeLanguage(lng);
    const newParams = new URLSearchParams(searchParams);
    newParams.set('lang', lng);
    navigate({ search: newParams.toString() });
    setSelected(lng);
    dispatch(setLanguage({ lng }));
    setOpen(false);
  };

  return (
    <div ref={ref} style={{ position: 'relative', flexShrink: 0 }}>
      <button
        onClick={() => setOpen(!open)}
        className="lp-lang-btn"
        style={{ ...SANS, background: 'none', border: '1px solid rgba(16,54,66,0.2)',
          borderRadius: 999, padding: '5px 12px', cursor: 'pointer',
          fontSize: 12, fontWeight: 700, letterSpacing: '0.08em',
          textTransform: 'uppercase', color: '#103642',
          display: 'flex', alignItems: 'center', gap: 5 }}
      >
        {selected.toUpperCase()}
        <svg width="10" height="6" viewBox="0 0 10 6" fill="none">
          <path d="M1 1l4 4 4-4" stroke="#103642" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      </button>
      {open && (
        <div style={{
          position: 'absolute', top: '100%', right: 0, marginTop: 4,
          background: '#fff', borderRadius: 10, border: '1px solid #f0f0f0',
          boxShadow: '0 4px 16px rgba(0,0,0,0.1)',
          overflow: 'hidden', minWidth: 110, zIndex: 200,
        }}>
          {[
            { code: 'de', label: 'Deutsch' },
            { code: 'en', label: 'English' },
          ].map(({ code, label }) => (
            <button
              key={code}
              onClick={() => switchTo(code)}
              style={{ ...SANS, display: 'flex', alignItems: 'center', gap: 8,
                width: '100%', padding: '10px 14px', border: 'none',
                background: selected === code ? '#f5f7f7' : '#fff',
                cursor: 'pointer', fontSize: 13, color: '#103642',
                fontWeight: selected === code ? 600 : 400, textAlign: 'left' }}
            >
              <i className={`flag-icon flag-icon-${code === 'en' ? 'us' : 'de'}`} />
              {label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

const PractitionerBadge = () => {
  const { t } = useTranslation();
  return (
  <div style={{
    border: '1px solid rgba(16,54,66,0.15)', borderRadius: 10,
    padding: '7px 12px', display: 'flex', alignItems: 'center', gap: 10,
  }}>
    <div style={{ display: 'flex', alignItems: 'center', gap: 5, flexShrink: 0 }}>
      <svg width="11" height="24" viewBox="0 0 16 34" fill="none" aria-hidden="true">
        <path d="M9 33 C8.5 26 8 18 9 3" stroke="#103642" strokeWidth="1" strokeLinecap="round" strokeOpacity="0.35"/>
        <path d="M9 29 C5 27 1 22 3 17 C6 19 9 24 9 29Z" fill="#103642" fillOpacity="0.6"/>
        <path d="M9 22 C5 20 1 15 3 10 C6 12 9 17 9 22Z" fill="#103642" fillOpacity="0.6"/>
        <path d="M9 15 C5 13 2 8 4 4 C7 6 9 11 9 15Z" fill="#103642" fillOpacity="0.55"/>
        <path d="M9 8 C7 7 5 4 7 2 C8 4 9 6 9 8Z" fill="#103642" fillOpacity="0.5"/>
      </svg>
      <div style={{ textAlign: 'center' }}>
        <p style={{ ...SANS, fontSize: 8, fontWeight: 800, letterSpacing: '0.1em',
          textTransform: 'uppercase', color: '#103642', margin: 0, lineHeight: 1.3 }}>
          {t('LP_BadgeLabelLine1')}
        </p>
        <p style={{ ...SANS, fontSize: 8, fontWeight: 800, letterSpacing: '0.1em',
          textTransform: 'uppercase', color: '#103642', margin: 0, lineHeight: 1.3 }}>
          {t('LP_BadgeLabelLine2')}
        </p>
      </div>
      <svg width="11" height="24" viewBox="0 0 16 34" fill="none" aria-hidden="true" style={{ transform: 'scaleX(-1)' }}>
        <path d="M9 33 C8.5 26 8 18 9 3" stroke="#103642" strokeWidth="1" strokeLinecap="round" strokeOpacity="0.35"/>
        <path d="M9 29 C5 27 1 22 3 17 C6 19 9 24 9 29Z" fill="#103642" fillOpacity="0.6"/>
        <path d="M9 22 C5 20 1 15 3 10 C6 12 9 17 9 22Z" fill="#103642" fillOpacity="0.6"/>
        <path d="M9 15 C5 13 2 8 4 4 C7 6 9 11 9 15Z" fill="#103642" fillOpacity="0.55"/>
        <path d="M9 8 C7 7 5 4 7 2 C8 4 9 6 9 8Z" fill="#103642" fillOpacity="0.5"/>
      </svg>
    </div>
    <div style={{ width: '1px', height: 26, background: 'rgba(16,54,66,0.15)', flexShrink: 0 }} />
    <p style={{ ...SANS, fontSize: 11, fontWeight: 600, color: '#103642',
      margin: 0, lineHeight: 1.4 }}>
      {t('LP_BadgeText')}
    </p>
  </div>
  );
};

const StickyBar = ({ t, K, isDesktop }) => {
  const { metaEvent } = useMeta();
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const onScroll = () => setVisible(window.scrollY > 600);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return (
    <div style={{
      position: 'fixed', bottom: 0, left: 0, right: 0, zIndex: 100,
      background: '#103642', padding: isDesktop ? '14px 40px' : '14px 20px',
      boxShadow: '0 -4px 24px rgba(0,0,0,0.18)',
      transform: visible ? 'translateY(0)' : 'translateY(100%)',
      transition: 'transform 0.3s ease',
    }}>
      <div style={{
        maxWidth: isDesktop ? 1200 : '100%',
        margin: '0 auto',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12,
      }}>
        <p style={{ ...SANS, fontSize: 14, color: 'rgba(255,255,255,0.8)',
          margin: 0, lineHeight: 1.3, flex: 1 }}>
          Your formula.<br />
          <span style={{ color: '#F6E147', fontWeight: 600 }}>Built for your body.</span>
        </p>
        <CustomLink to="/quiz" onClick={() => metaEvent('Lead')} className="lp-sticky-cta" style={{
          ...SANS, background: '#F6E147', color: '#103642',
          fontWeight: 700, fontSize: 15, borderRadius: 999,
          padding: '12px 22px', textDecoration: 'none',
          whiteSpace: 'nowrap', flexShrink: 0,
          display: 'flex', alignItems: 'center', gap: 6,
        }}>
          {t(K.HeroCta)} →
        </CustomLink>
      </div>
    </div>
  );
};

const WrongCombination = () => {
  const { t } = useTranslation();
  const reviewStats = useReviewStats();
  const [isDesktop, setIsDesktop] = useState(
    typeof window !== 'undefined' && window.innerWidth >= 1024
  );

  useEffect(() => {
    const handleResize = () => setIsDesktop(window.innerWidth >= 1024);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const maxW = isDesktop ? 1200 : 480;
  const px = isDesktop ? 80 : 24;

  return (
    <div style={{ minHeight: '100vh', background: '#fff', ...SANS, paddingBottom: 72 }}>
      <Helmet>
        <title>Is your supplement combination right? | riise.</title>
        <meta name="robots" content="noindex" />
      </Helmet>

      <DiscountBanner />

      {/* ── Header ── */}
      <header style={{ background: '#fff', borderBottom: '1px solid #f0f0f0',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: isDesktop ? '18px 80px' : '16px 20px' }}>
        <div style={{ width: 60, flexShrink: 0 }} />
        <img src={logo_home} alt="riise" style={{ height: 24, width: 'auto' }} />
        <LangSwitcher />
      </header>

      {/* ── Hero ── */}
      {isDesktop ? (
        <section style={{ background: '#F9F9F9', overflow: 'hidden' }}>
          <div style={{
            maxWidth: 1400, margin: '0 auto',
            display: 'grid', gridTemplateColumns: '52% 48%',
            minHeight: 600,
          }}>
            {/* Left: content */}
            <div style={{ padding: '56px 64px 56px 80px', display: 'flex', alignItems: 'center' }}>
              <Stack gap={24} style={{ width: '100%', maxWidth: 560 }}>
                <PractitionerBadge />
                <h1 style={{ ...SERIF, fontSize: 58, lineHeight: 1.06, fontWeight: 400,
                  color: '#103642', margin: 0 }}>
                  {t(K.HeroH1Part1)}<br />
                  <em style={{ fontStyle: 'italic', color: '#116D6E' }}>{t(K.HeroH1Part2)}</em>
                </h1>
                <p style={{ ...SANS, fontSize: 18, color: 'rgba(16,54,66,0.7)',
                  lineHeight: 1.7, margin: 0 }}>
                  {t(K.HeroSubline)}
                </p>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <StarRow />
                  <p style={{ ...SANS, fontSize: 15, color: 'rgba(16,54,66,0.6)', margin: 0 }}>
                    {t(K.HeroSocialProof, {
                      rating: reviewStats?.average || '4.88',
                      count: reviewStats?.count ? reviewStats.count.toLocaleString('en') : '5,000+',
                    })}
                  </p>
                </div>
                <Stack gap={12} style={{ maxWidth: 380 }}>
                  <CtaButton full={false}>{t(K.HeroCta)}</CtaButton>
                  <p style={{ ...SANS, fontSize: 13, color: 'rgba(16,54,66,0.4)',
                    margin: 0, textAlign: 'center' }}>
                    {t(K.CtaSub)}
                  </p>
                </Stack>
              </Stack>
            </div>
            {/* Right: image */}
            <div style={{ position: 'relative', overflow: 'hidden', background: '#F5F8F5' }}>
              <img src={HeroImage} alt="riise personalised sachet"
                style={{ width: '100%', height: '100%', objectFit: 'contain',
                  objectPosition: 'center', display: 'block' }} loading="eager" />
            </div>
          </div>
        </section>
      ) : (
        <section style={{ background: '#F9F9F9' }}>
          <div style={{ maxWidth: 480, margin: '0 auto', padding: '28px 24px 40px' }}>
            <Stack gap={20}>
              <PractitionerBadge />
              <h1 style={{ ...SERIF, fontSize: 40, lineHeight: 1.08, fontWeight: 400,
                color: '#103642', margin: 0 }}>
                {t(K.HeroH1Part1)}{' '}
                <em style={{ fontStyle: 'italic', color: '#116D6E' }}>{t(K.HeroH1Part2)}</em>
              </h1>
              <p style={{ ...SANS, fontSize: 16, color: 'rgba(16,54,66,0.7)',
                lineHeight: 1.7, margin: 0 }}>
                {t(K.HeroSubline)}
              </p>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <StarRow />
                <p style={{ ...SANS, fontSize: 14, color: 'rgba(16,54,66,0.6)', margin: 0 }}>
                  {t(K.HeroSocialProof, {
                    rating: reviewStats?.average || '4.88',
                    count: reviewStats?.count ? reviewStats.count.toLocaleString('en') : '5,000+',
                  })}
                </p>
              </div>
              <Stack gap={12}>
                <CtaButton>{t(K.HeroCta)}</CtaButton>
                <p style={{ ...SANS, fontSize: 13, color: 'rgba(16,54,66,0.4)',
                  textAlign: 'center', margin: 0 }}>
                  {t(K.CtaSub)}
                </p>
              </Stack>
            </Stack>
          </div>
          <img src={HeroImage} alt="riise personalised sachet"
            style={{ width: '100%', height: 'auto', display: 'block' }} loading="eager" />
        </section>
      )}

      {/* ── Review ticker ── */}
      <div style={{ background: '#103642', padding: '8px 0', overflow: 'hidden' }}>
        <style>{`@keyframes riiseMarquee { from { transform: translateX(0); } to { transform: translateX(-50%); } }`}</style>
        <div
          style={{ display: 'flex', whiteSpace: 'nowrap', width: 'max-content',
            animation: 'riiseMarquee 45s linear infinite' }}
        >
          {[0, 1].map((dup) => (
            <div key={dup} style={{ display: 'flex', alignItems: 'center',
              color: '#F9F9F9', fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: 14 }}>
              {[
                t(Review1),
                t(Review2),
                t(Review3),
                t(Review4),
                <span key="rating" style={{ color: '#F6E147', fontWeight: 600 }}>
                  {t(ReviewRating, { rating: reviewStats?.average || '4.88' })}
                </span>,
              ].map((item, i) => (
                <React.Fragment key={i}>
                  <span style={{ padding: '0 16px' }}>{item}</span>
                  <span style={{ color: '#F6E147' }}>·</span>
                </React.Fragment>
              ))}
            </div>
          ))}
        </div>
      </div>

      {/* ── Stats strip ── */}
      <section style={{ background: '#103642', padding: `56px ${px}px` }}>
        <div style={{
          maxWidth: maxW, margin: '0 auto',
          display: 'grid',
          gridTemplateColumns: isDesktop ? '1fr 1fr 1fr 1fr' : '1fr 1fr',
          gap: isDesktop ? '0 40px' : '48px 0',
        }}>
          {[
            { val: K.Stat1Val, label: K.Stat1Label },
            { val: K.Stat2Val, label: K.Stat2Label },
            { val: K.Stat3Val, label: K.Stat3Label },
            { val: K.Stat4Val, label: K.Stat4Label },
          ].map(({ val, label }) => (
            <Stack key={val} gap={8} align="center" className="lp-stat" style={{ textAlign: 'center' }}>
              <p style={{ ...SERIF, fontSize: isDesktop ? 60 : 52, lineHeight: 1, fontWeight: 400,
                color: '#F6E147', margin: 0 }}>
                {t(val)}
              </p>
              <p style={{ ...SANS, fontSize: 13, color: 'rgba(255,255,255,0.6)',
                lineHeight: 1.4, maxWidth: 140, margin: 0 }}>
                {t(label)}
              </p>
            </Stack>
          ))}
        </div>
      </section>

      {/* ── Problem section ── */}
      <Section bg="#f5f7f7" maxW={maxW} px={px}>
        <Stack gap={20}>
          <Label>{t(K.ProbSectionLabel)}</Label>

          <h2 style={{ ...SERIF, fontSize: isDesktop ? 36 : 28, lineHeight: 1.15, fontWeight: 400,
            color: '#103642', margin: 0, textAlign: 'center' }}>
            {t(K.ProbH2Line1)}<br />
            <em style={{ fontStyle: 'italic', color: '#116D6E' }}>{t(K.ProbH2Line2)}</em>
          </h2>

          <p style={{ ...SANS, fontSize: 16, color: 'rgba(16,54,66,0.6)',
            lineHeight: 1.7, margin: 0, textAlign: 'center',
            maxWidth: isDesktop ? 640 : undefined, alignSelf: isDesktop ? 'center' : undefined }}>
            {t(K.ProbSubline)}
          </p>

          <div style={{
            marginTop: 8,
            display: isDesktop ? 'grid' : 'flex',
            gridTemplateColumns: isDesktop ? '1fr 1fr' : undefined,
            flexDirection: isDesktop ? undefined : 'column',
            gap: 16,
          }}>
            {/* Old way */}
            <div className="lp-card" style={{ borderRadius: 16, padding: isDesktop ? 32 : 24, background: '#e8e4df' }}>
              <p style={{ ...SANS, fontSize: 12, fontWeight: 700, letterSpacing: '0.1em',
                textTransform: 'uppercase', color: 'rgba(16,54,66,0.5)',
                margin: '0 0 20px 0' }}>
                {t(K.OldWayTitle)}
              </p>
              <Stack gap={16}>
                {[K.OldWay1, K.OldWay2, K.OldWay3, K.OldWay4, K.OldWay5].map((key) => (
                  <div key={key} style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
                    <span style={{ ...SANS, fontSize: 13, color: 'rgba(16,54,66,0.4)',
                      fontWeight: 700, flexShrink: 0, marginTop: 1 }}>✕</span>
                    <p style={{ ...SANS, fontSize: 15, color: 'rgba(16,54,66,0.6)',
                      margin: 0, lineHeight: 1.5 }}>{t(key)}</p>
                  </div>
                ))}
              </Stack>
            </div>

            {/* riise way */}
            <div className="lp-card lp-card-riise" style={{ borderRadius: 16, padding: isDesktop ? 32 : 24, background: '#fff',
              boxShadow: '0 4px 20px rgba(0,0,0,0.08)', border: '1px solid rgba(17,109,110,0.1)' }}>
              <p style={{ ...SANS, fontSize: 12, fontWeight: 700, letterSpacing: '0.1em',
                textTransform: 'uppercase', color: '#103642', margin: '0 0 20px 0' }}>
                {t(K.NewWayTitle)}
              </p>
              <Stack gap={16}>
                {[K.NewWay1, K.NewWay2, K.NewWay3, K.NewWay4, K.NewWay5].map((key) => (
                  <div key={key} style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
                    <div style={{ flexShrink: 0, marginTop: 2 }}>
                      <Icon name="tealCheckCircle" size={18} />
                    </div>
                    <p style={{ ...SANS, fontSize: 15, color: '#103642',
                      margin: 0, lineHeight: 1.5 }}>{t(key)}</p>
                  </div>
                ))}
              </Stack>
            </div>
          </div>
        </Stack>
      </Section>

      {/* ── Results ── */}
      <Section bg="#fff" maxW={maxW} px={px}>
        <Stack gap={20}>
          <Label pill>{t(K.ResultsSectionLabel)}</Label>

          <h2 style={{ ...SERIF, fontSize: isDesktop ? 36 : 28, lineHeight: 1.15, fontWeight: 400,
            color: '#103642', margin: 0, textAlign: 'center' }}>
            {t(K.ResultsH1)}<br />
            <em style={{ fontStyle: 'italic', color: '#116D6E' }}>{t(K.ResultsH2)}</em>
          </h2>

          <div style={{
            marginTop: 12,
            display: isDesktop ? 'grid' : 'flex',
            gridTemplateColumns: isDesktop ? '1fr 1fr' : undefined,
            flexDirection: isDesktop ? undefined : 'column',
            gap: isDesktop ? 48 : 32,
          }}>
            {[
              { val: K.ResultsStat1Val, desc: K.ResultsStat1Desc },
              { val: K.ResultsStat2Val, desc: K.ResultsStat2Desc },
            ].map(({ val, desc }) => (
              <div key={val} style={{ display: 'flex', alignItems: 'center', gap: 24 }}>
                <p style={{ ...SERIF, fontSize: isDesktop ? 80 : 68, lineHeight: 1, fontWeight: 400,
                  color: '#103642', margin: 0, flexShrink: 0,
                  width: isDesktop ? 180 : 150, textAlign: 'right' }}>
                  {t(val)}
                </p>
                <p style={{ ...SANS, fontSize: 16, color: 'rgba(16,54,66,0.7)',
                  margin: 0, lineHeight: 1.5 }}>
                  {t(desc)}
                </p>
              </div>
            ))}
          </div>

          <p style={{ ...SANS, fontSize: 12, color: 'rgba(16,54,66,0.3)',
            textAlign: 'center', margin: 0, marginTop: 8 }}>
            {t(K.ResultsSource)}
          </p>
        </Stack>
      </Section>

      {/* ── Solution reveal ── */}
      <section style={{ background: '#f5f7f7', paddingTop: 64, paddingBottom: 32 }}>
        <div style={{ maxWidth: maxW, margin: '0 auto', padding: `0 ${px}px` }}>
          {isDesktop ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: 48, marginBottom: 48 }}>
              <img src={RiisePouch} alt="riise sachet"
                style={{ width: 140, height: 'auto', flexShrink: 0 }} />
              <div>
                <h2 style={{ ...SERIF, fontSize: 36, lineHeight: 1.15, fontWeight: 400,
                  color: '#103642', margin: '0 0 12px 0' }}>
                  {t(K.IngrHeading)}
                </h2>
                <p style={{ ...SANS, fontSize: 15, color: 'rgba(16,54,66,0.5)',
                  margin: 0, lineHeight: 1.6 }}>
                  No pills, 10 seconds per day
                </p>
              </div>
            </div>
          ) : (
            <Stack gap={24} align="center" style={{ textAlign: 'center', marginBottom: 36 }}>
              <img src={RiisePouch} alt="riise sachet"
                style={{ width: 150, height: 'auto' }} />
              <h2 style={{ ...SERIF, fontSize: 28, lineHeight: 1.15, fontWeight: 400,
                color: '#103642', margin: 0 }}>
                {t(K.IngrHeading)}
              </h2>
              <p style={{ ...SANS, fontSize: 13, color: 'rgba(16,54,66,0.5)',
                margin: 0, lineHeight: 1.6 }}>
                No pills, 10 seconds per day
              </p>
            </Stack>
          )}
        </div>
        <IngredientsSlider />
      </section>

      {/* ── How it works ── */}
      <Section bg="#fff" maxW={maxW} px={px}>
        <Stack gap={isDesktop ? 48 : 40}>
          <h2 style={{ ...SERIF, fontSize: isDesktop ? 36 : 28, lineHeight: 1.15, fontWeight: 400,
            color: '#103642', margin: 0, textAlign: 'center' }}>
            {t(K.HowWorksHeading)}
          </h2>

          {isDesktop ? (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 32 }}>
              {[
                { n: 1, title: K.Step1Title, desc: K.Step1Desc },
                { n: 2, title: K.Step2Title, desc: K.Step2Desc },
                { n: 3, title: K.Step3Title, desc: K.Step3Desc },
              ].map(({ n, title, desc }) => (
                <div key={n} className="lp-card" style={{
                  background: '#f5f7f7', borderRadius: 16, padding: 28,
                  display: 'flex', flexDirection: 'column', gap: 16,
                }}>
                  <StepNumber n={n} />
                  <p style={{ ...SANS, fontSize: 17, fontWeight: 600,
                    color: '#103642', margin: 0 }}>
                    {t(title)}
                  </p>
                  <p style={{ ...SANS, fontSize: 15, color: 'rgba(16,54,66,0.6)',
                    margin: 0, lineHeight: 1.65 }}>
                    {t(desc)}
                  </p>
                </div>
              ))}
            </div>
          ) : (
            <Stack gap={32}>
              {[
                { n: 1, title: K.Step1Title, desc: K.Step1Desc },
                { n: 2, title: K.Step2Title, desc: K.Step2Desc },
                { n: 3, title: K.Step3Title, desc: K.Step3Desc },
              ].map(({ n, title, desc }) => (
                <div key={n} style={{ display: 'flex', alignItems: 'flex-start', gap: 20 }}>
                  <StepNumber n={n} />
                  <Stack gap={8}>
                    <p style={{ ...SANS, fontSize: 16, fontWeight: 600,
                      color: '#103642', margin: 0 }}>
                      {t(title)}
                    </p>
                    <p style={{ ...SANS, fontSize: 15, color: 'rgba(16,54,66,0.6)',
                      margin: 0, lineHeight: 1.65 }}>
                      {t(desc)}
                    </p>
                  </Stack>
                </div>
              ))}
            </Stack>
          )}
        </Stack>
      </Section>

      {/* ── Mid-page CTA ── */}
      <Section bg="#103642" maxW={maxW} px={px}>
        <Stack gap={24} align="center" style={{ textAlign: 'center' }}>
          <h2 style={{ ...SERIF, fontSize: isDesktop ? 36 : 28, lineHeight: 1.15, fontWeight: 400,
            color: '#fff', margin: 0 }}>
            {t(K.FinalHeading)}
          </h2>
          <p style={{ ...SANS, fontSize: 16, color: 'rgba(255,255,255,0.65)',
            margin: 0, lineHeight: 1.7, maxWidth: isDesktop ? 560 : undefined }}>
            {t(K.FinalSubline)}
          </p>
          <div style={{ width: isDesktop ? 'auto' : '100%' }}>
            <CtaButton full={!isDesktop}>{t(K.HeroCta)}</CtaButton>
          </div>
        </Stack>
      </Section>

      {/* ── Expert ── */}
      <Section bg="#fff" maxW={maxW} px={px}>
        <Stack gap={32}>
          <h2 style={{ ...SERIF, fontSize: isDesktop ? 36 : 28, lineHeight: 1.15, fontWeight: 400,
            color: '#103642', margin: 0, textAlign: 'center' }}>
            {t(K.ExpertHeading)}
          </h2>
          <ExpertSection />
          <div style={{ display: 'flex', justifyContent: 'center' }}>
            <div style={{ width: '100%', maxWidth: 450 }}>
              <PractitionerBadge />
            </div>
          </div>
        </Stack>
      </Section>

      {/* ── Featured testimonial (Jan) ── */}
      <Section bg="#f5f7f7" py={56} maxW={isDesktop ? 800 : 480} px={px}>
        <Stack gap={20}>
          <StarRow count={5} />
          <p style={{ ...SANS, fontSize: isDesktop ? 22 : 18, lineHeight: 1.6, fontWeight: 400,
            color: '#103642', margin: 0, fontStyle: 'italic' }}>
            "{t(K.FeatQuote)}"
          </p>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <img src={JanPhoto} alt="Jan"
              style={{ width: 48, height: 48, borderRadius: '50%',
                objectFit: 'cover', flexShrink: 0 }} />
            <p style={{ ...SANS, fontSize: 14, fontWeight: 600,
              color: 'rgba(16,54,66,0.6)', margin: 0 }}>
              {t(K.FeatAuthor)}
            </p>
          </div>
        </Stack>
      </Section>

      {/* ── Video testimonials ── */}
      <BoomTestimonials />

      {/* ── Comparison table ── */}
      <Section bg="#f5f7f7" maxW={maxW} px={px}>
        <MakeChoice />
      </Section>

      {/* ── FAQ ── */}
      <Section bg="#fff" maxW={isDesktop ? 800 : 480} px={px}>
        <Stack gap={32}>
          <h2 style={{ ...SERIF, fontSize: isDesktop ? 36 : 28, lineHeight: 1.15, fontWeight: 400,
            color: '#103642', margin: 0 }}>
            {t(K.FaqHeading)}
          </h2>
          <div style={{ display: 'flex', flexDirection: 'column',
            borderTop: '1px solid #f0f0f0' }}>
            {[
              { q: K.Faq1Q, a: K.Faq1A },
              { q: K.Faq2Q, a: K.Faq2A },
              { q: K.Faq3Q, a: K.Faq3A },
              { q: K.Faq4Q, a: K.Faq4A },
              { q: K.Faq5Q, a: K.Faq5A },
            ].map(({ q, a }) => (
              <div key={q} className="lp-faq-row" style={{ borderBottom: '1px solid #f0f0f0', padding: '4px 0' }}>
                <Accordion heading={t(q)}>
                  <p style={{ ...SANS, fontSize: 15, color: 'rgba(16,54,66,0.7)',
                    lineHeight: 1.65, margin: '12px 0 16px 0' }}>
                    {t(a)}
                  </p>
                </Accordion>
              </div>
            ))}
          </div>
        </Stack>
      </Section>

      {/* ── Final CTA ── */}
      <section style={{ background: '#103642', padding: `80px ${px}px` }}>
        <div style={{ maxWidth: maxW, margin: '0 auto' }}>
          <Stack gap={24} align="center" style={{ textAlign: 'center' }}>
            <h2 style={{ ...SERIF, fontSize: isDesktop ? 40 : 28, lineHeight: 1.15, fontWeight: 400,
              color: '#fff', margin: 0 }}>
              {t(K.FinalHeading)}
            </h2>
            <p style={{ ...SANS, fontSize: 16, color: 'rgba(255,255,255,0.7)',
              margin: 0, lineHeight: 1.7, maxWidth: isDesktop ? 560 : undefined }}>
              {t(K.FinalSubline)}
            </p>
            <Stack gap={12} style={{ width: isDesktop ? 'auto' : '100%' }}>
              <CtaButton full={!isDesktop}>{t(K.FinalCta)}</CtaButton>
              <p style={{ ...SANS, fontSize: 13, color: 'rgba(255,255,255,0.4)',
                textAlign: 'center', margin: 0 }}>
                {t(K.CtaSub)}
              </p>
            </Stack>
          </Stack>
        </div>
      </section>

      {/* ── Reviews carousel ── */}
      <Section bg="#f5f7f7" maxW={maxW} px={px}>
        <Stack gap={32}>
          <h2 style={{ ...SERIF, fontSize: isDesktop ? 36 : 28, lineHeight: 1.15, fontWeight: 400,
            color: '#103642', margin: 0, textAlign: 'center' }}>
            {t(K.ReviewsHeading)}
          </h2>
          <ReviewsMobile />
        </Stack>
      </Section>

      <StickyBar t={t} K={K} isDesktop={isDesktop} />

      {/* ── Footer ── */}
      <footer style={{ background: '#fff', borderTop: '1px solid #f0f0f0',
        padding: isDesktop ? '40px 80px' : '40px 24px' }}>
        <div style={{ maxWidth: maxW, margin: '0 auto' }}>
          <Stack gap={16} align="center">
            <img src={logo_home} alt="riise"
              style={{ height: 20, width: 'auto', opacity: 0.4 }} />
            <div style={{ display: 'flex', gap: 20, flexWrap: 'wrap', justifyContent: 'center' }}>
              <CustomLink to="/impressum"
                style={{ ...SANS, fontSize: 12, color: 'rgba(16,54,66,0.4)', textDecoration: 'none' }}>
                Impressum
              </CustomLink>
              <CustomLink to="/policy"
                style={{ ...SANS, fontSize: 12, color: 'rgba(16,54,66,0.4)', textDecoration: 'none' }}>
                Privacy Policy
              </CustomLink>
            </div>
            <p style={{ ...SANS, fontSize: 11, color: 'rgba(16,54,66,0.3)',
              textAlign: 'center', margin: 0 }}>
              {t(K.AdDisclosure)} · {t('LP_SupplementDisclaimer')}
            </p>
          </Stack>
        </div>
      </footer>
    </div>
  );
};

export default WrongCombination;
